import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  fireConfetti,
  fireFireworks,
  fireCoins,
  fireStars,
  RocketCelebration,
  ATHCelebration,
  TargetReachedCelebration,
  CelebrationOptions,
} from './Celebrations';

// =============================================================================
// CELEBRATION CONTEXT
// =============================================================================

interface CelebrationContextType {
  // Basic celebrations
  triggerConfetti: (options?: CelebrationOptions) => void;
  triggerFireworks: (duration?: number) => void;
  triggerCoins: (options?: CelebrationOptions) => void;
  triggerStars: (options?: CelebrationOptions) => void;

  // Complex celebrations
  triggerRocket: (message?: string, percentGain?: number) => void;
  triggerATH: (tokenSymbol?: string, newATH?: string) => void;
  triggerTargetReached: (targetName?: string, targetValue?: string) => void;

  // Milestone celebration (auto-selects based on achievement)
  triggerMilestone: (type: MilestoneType, data?: MilestoneData) => void;
}

export type MilestoneType =
  | 'ath'           // All-Time High
  | 'target'        // Price target reached
  | 'gain_10'       // +10% gain
  | 'gain_25'       // +25% gain
  | 'gain_50'       // +50% gain
  | 'gain_100'      // +100% gain (2x)
  | 'first_profit'  // First profitable trade
  | 'streak'        // Win streak
  | 'portfolio_ath' // Portfolio ATH
  | 'transaction';  // Successful transaction

interface MilestoneData {
  tokenSymbol?: string;
  value?: string;
  percentGain?: number;
  streakCount?: number;
}

const CelebrationContext = createContext<CelebrationContextType | null>(null);

// =============================================================================
// CELEBRATION PROVIDER
// =============================================================================

interface CelebrationProviderProps {
  children: React.ReactNode;
}

export const CelebrationProvider: React.FC<CelebrationProviderProps> = ({
  children,
}) => {
  // Rocket state
  const [showRocket, setShowRocket] = useState(false);
  const [rocketMessage, setRocketMessage] = useState('+10% 🚀');

  // ATH state
  const [showATH, setShowATH] = useState(false);
  const [athData, setAthData] = useState({ symbol: '', value: '' });

  // Target state
  const [showTarget, setShowTarget] = useState(false);
  const [targetData, setTargetData] = useState({ name: '', value: '' });

  // Prevent duplicate triggers
  const lastTrigger = useRef<{ type: string; time: number }>({ type: '', time: 0 });

  const canTrigger = (type: string): boolean => {
    const now = Date.now();
    if (lastTrigger.current.type === type && now - lastTrigger.current.time < 3000) {
      return false;
    }
    lastTrigger.current = { type, time: now };
    return true;
  };

  // Basic celebrations
  const triggerConfetti = useCallback((options?: CelebrationOptions) => {
    if (canTrigger('confetti')) {
      fireConfetti(options);
    }
  }, []);

  const triggerFireworks = useCallback((duration?: number) => {
    if (canTrigger('fireworks')) {
      fireFireworks(duration);
    }
  }, []);

  const triggerCoins = useCallback((options?: CelebrationOptions) => {
    if (canTrigger('coins')) {
      fireCoins(options);
    }
  }, []);

  const triggerStars = useCallback((options?: CelebrationOptions) => {
    if (canTrigger('stars')) {
      fireStars(options);
    }
  }, []);

  // Complex celebrations
  const triggerRocket = useCallback((message?: string, percentGain?: number) => {
    if (!canTrigger('rocket')) return;

    const displayMessage = message || (percentGain ? `+${Math.round(percentGain)}% 🚀` : '+10% 🚀');
    setRocketMessage(displayMessage);
    setShowRocket(true);
  }, []);

  const triggerATH = useCallback((tokenSymbol?: string, newATH?: string) => {
    if (!canTrigger('ath')) return;

    setAthData({
      symbol: tokenSymbol || 'Portfolio',
      value: newATH || '',
    });
    setShowATH(true);
  }, []);

  const triggerTargetReached = useCallback((targetName?: string, targetValue?: string) => {
    if (!canTrigger('target')) return;

    setTargetData({
      name: targetName || 'Target',
      value: targetValue || '',
    });
    setShowTarget(true);
  }, []);

  // Milestone celebration (smart selection)
  const triggerMilestone = useCallback((type: MilestoneType, data?: MilestoneData) => {
    switch (type) {
      case 'ath':
      case 'portfolio_ath':
        triggerATH(data?.tokenSymbol, data?.value);
        break;

      case 'target':
        triggerTargetReached(data?.tokenSymbol, data?.value);
        break;

      case 'gain_10':
        triggerRocket(undefined, data?.percentGain || 10);
        break;

      case 'gain_25':
        triggerRocket(`+${Math.round(data?.percentGain || 25)}% 🔥`, data?.percentGain || 25);
        fireConfetti({ intensity: 'medium' });
        break;

      case 'gain_50':
        triggerRocket(`+${Math.round(data?.percentGain || 50)}% 🔥🔥`, data?.percentGain || 50);
        fireConfetti({ intensity: 'high' });
        break;

      case 'gain_100':
        triggerRocket(`2x 🚀🚀`, data?.percentGain || 100);
        setTimeout(() => fireFireworks(3000), 500);
        break;

      case 'first_profit':
        fireConfetti({ intensity: 'medium' });
        fireStars();
        break;

      case 'streak':
        if (data?.streakCount && data.streakCount >= 5) {
          fireFireworks(2000);
        } else {
          fireStars();
        }
        break;

      case 'transaction':
        fireCoins();
        break;
    }
  }, [triggerATH, triggerTargetReached, triggerRocket]);

  const contextValue: CelebrationContextType = {
    triggerConfetti,
    triggerFireworks,
    triggerCoins,
    triggerStars,
    triggerRocket,
    triggerATH,
    triggerTargetReached,
    triggerMilestone,
  };

  return (
    <CelebrationContext.Provider value={contextValue}>
      {children}

      {/* Global Celebration Overlays */}
      <AnimatePresence>
        {showRocket && (
          <RocketCelebration
            show={showRocket}
            onComplete={() => setShowRocket(false)}
            message={rocketMessage}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showATH && (
          <ATHCelebration
            show={showATH}
            onComplete={() => setShowATH(false)}
            tokenSymbol={athData.symbol}
            newATH={athData.value}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showTarget && (
          <TargetReachedCelebration
            show={showTarget}
            onComplete={() => setShowTarget(false)}
            targetName={targetData.name}
            targetValue={targetData.value}
          />
        )}
      </AnimatePresence>
    </CelebrationContext.Provider>
  );
};

// =============================================================================
// HOOK
// =============================================================================

export const useCelebrations = (): CelebrationContextType => {
  const context = useContext(CelebrationContext);

  if (!context) {
    // Return no-op functions if not in provider (for safety)
    return {
      triggerConfetti: () => {},
      triggerFireworks: () => {},
      triggerCoins: () => {},
      triggerStars: () => {},
      triggerRocket: () => {},
      triggerATH: () => {},
      triggerTargetReached: () => {},
      triggerMilestone: () => {},
    };
  }

  return context;
};

// =============================================================================
// PRICE CHANGE DETECTOR HOOK
// =============================================================================

interface PriceChangeOptions {
  enableCelebrations?: boolean;
  athThreshold?: number; // Price must exceed previous ATH by this %
  targetPrices?: { [tokenSymbol: string]: number[] };
}

export const usePriceChangeDetector = (
  currentPrices: { [symbol: string]: number },
  options: PriceChangeOptions = {}
) => {
  const { enableCelebrations = true } = options;
  const celebrations = useCelebrations();
  const previousPrices = useRef<{ [symbol: string]: number }>({});
  const athPrices = useRef<{ [symbol: string]: number }>({});

  const checkPriceChanges = useCallback(() => {
    if (!enableCelebrations) return;

    Object.entries(currentPrices).forEach(([symbol, price]) => {
      const prevPrice = previousPrices.current[symbol];
      const athPrice = athPrices.current[symbol] || 0;

      if (prevPrice !== undefined) {
        const percentChange = ((price - prevPrice) / prevPrice) * 100;

        // Check for significant gains
        if (percentChange >= 100) {
          celebrations.triggerMilestone('gain_100', { tokenSymbol: symbol, percentGain: percentChange });
        } else if (percentChange >= 50) {
          celebrations.triggerMilestone('gain_50', { tokenSymbol: symbol, percentGain: percentChange });
        } else if (percentChange >= 25) {
          celebrations.triggerMilestone('gain_25', { tokenSymbol: symbol, percentGain: percentChange });
        } else if (percentChange >= 10) {
          celebrations.triggerMilestone('gain_10', { tokenSymbol: symbol, percentGain: percentChange });
        }
      }

      // Check for ATH
      if (price > athPrice) {
        athPrices.current[symbol] = price;
        if (athPrice > 0) {
          // Only celebrate if we had a previous ATH (not first price)
          celebrations.triggerATH(symbol, `$${price.toLocaleString()}`);
        }
      }

      previousPrices.current[symbol] = price;
    });
  }, [currentPrices, celebrations, enableCelebrations]);

  return { checkPriceChanges };
};

export default CelebrationProvider;
